/**
 * Rotas da aplicação.
 *
 * `ProtectedRoute` garante que nenhuma tela interna renderize sem sessão. Isso é
 * conveniência de navegação: a API rejeita a requisição de todo modo.
 */

import { Link, Navigate, createBrowserRouter, useLocation } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { lazy, Suspense, type ReactNode } from 'react';
import { AppShell } from './components/AppShell';
import { PERMISSOES_ADMIN } from './components/MenuDoUsuario';
import { Spinner } from './components/ui';
import { useSession } from './lib/session';
import { capacidadeDaRota } from './lib/capacidade-da-rota';
import { t } from './lib/i18n';

import { LoginPage } from './pages/LoginPage';
import { LegalPage } from './pages/LegalPage';
import { ApresentacaoPage } from './pages/ApresentacaoPage';
import { StatusPage } from './pages/StatusPage';
import { AcessibilidadePage } from './pages/AcessibilidadePage';
import { ContatoPage } from './pages/ContatoPage';
import { AceiteDeTermosPage } from './pages/AceiteDeTermosPage';
import { EscolherOrganizacaoPage } from './pages/EscolherOrganizacaoPage';
import { CriarContaPage } from './pages/CriarContaPage';
import {
  RedirecionarFichaDaOferta,
  RedirecionarSecaoDoPlaybook,
} from './components/RedirecionamentosDoPlaybook';
import { ConfirmarCadastroPage } from './pages/ConfirmarCadastroPage';
import { CadastroDaEmpresaPage } from './pages/CadastroDaEmpresaPage';
import { PasswordResetPage } from './pages/PasswordResetPage';
import { ConectarAgentePage } from './pages/ConectarAgentePage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { MfaChallengePage } from './pages/MfaChallengePage';
import { MfaSetupPage } from './pages/MfaSetupPage';
import { NotFoundPage } from './pages/NotFoundPage';

/*
 * AS TELAS INTERNAS CHEGAM SOB DEMANDA (CERES-EST-003-CORRECAO-01 §9).
 *
 * O QUE ESTAVA ERRADO
 *
 * O roteador importava as oitenta e uma telas de forma estática, e o Rollup
 * juntava tudo num pacote só. Quem abria a TELA DE LOGIN baixava o CRM
 * inteiro — Kanban, editor de proposta, gráficos —
 * antes de digitar a senha. Em rede móvel isso é um minuto olhando para uma
 * tela branca, para ver um formulário de dois campos.
 *
 * O QUE FICA ESTÁTICO, E POR QUÊ
 *
 * O fluxo de ENTRADA: login, portão legal, troca de senha, segundo fator,
 * escolha de organização e o painel. São as telas que a pessoa vê nos
 * primeiros segundos, e adiá-las trocaria um problema por outro — um piscar
 * de carregamento no caminho mais percorrido do produto.
 *
 * O resto carrega quando alguém vai até lá. O `<Suspense>` que segura a
 * espera fica dentro do `AppShell`, no lugar do `<Outlet>`: o menu e o
 * cabeçalho continuam na tela enquanto o miolo carrega, em vez de a
 * aplicação inteira sumir e voltar.
 */
const MedidasAdminPage = lazy(async () => ({
  default: (await import('./pages/MedidasAdminPage')).MedidasAdminPage,
}));
const CatalogoMetodoPage = lazy(async () => ({
  default: (await import('./pages/CatalogoMetodoPage')).CatalogoMetodoPage,
}));
const CatalogoBibliotecaPage = lazy(async () => ({
  default: (await import('./pages/CatalogoBibliotecaPage')).CatalogoBibliotecaPage,
}));
const CatalogoGovernancaPage = lazy(async () => ({
  default: (await import('./pages/CatalogoGovernancaPage')).CatalogoGovernancaPage,
}));
// CERES-ADM-003: a administração da organização e os convites recebidos.
const AdministracaoDaOrganizacaoPage = lazy(async () => ({
  default: (await import('./pages/AdministracaoDaOrganizacaoPage')).AdministracaoDaOrganizacaoPage,
}));
const ConvitesRecebidosPage = lazy(async () => ({
  default: (await import('./pages/ConvitesRecebidosPage')).ConvitesRecebidosPage,
}));

/**
 * CERES-ADM-003 — UMA TELA PARA ADMINISTRAR PESSOAS.
 *
 * Usuário interno administra pela "Administração da organização". A tela
 * legada de usuários fica só para o administrador de CANAL, cuja gestão é
 * limitada ao próprio parceiro e não faz parte da administração do tenant.
 */
function UsuariosDoTenantOuDoCanal(): JSX.Element {
  const { user } = useSession();
  if (user?.partnerId === null) return <Navigate to="/admin/organizacao?aba=pessoas" replace />;
  return <UsersAdminPage />;
}
/*
 * O PAINEL TAMBÉM CHEGA SOB DEMANDA.
 *
 * Ele é a primeira tela DEPOIS do login, e não durante: quem está digitando a
 * senha não precisa do cockpit carregado. Ele puxa saúde de pipeline, jornada,
 * previsão e evolução — os gráficos e as tabelas que mais pesam —, e mantê-lo
 * estático deixava metade do que a divisão economizou de volta no pacote de
 * entrada. O `<Suspense>` do `AppShell` cobre a espera dele como cobre a das
 * outras.
 */
/*
 * CERES-UX-001: a raiz é HOJE (a fila de trabalho). O cockpit continua dentro
 * dela, como a visão Resultados.
 */
const HojePage = lazy(async () => ({
  default: (await import('./pages/HojePage')).HojePage,
}));
const AccountsPage = lazy(async () => ({
  default: (await import('./pages/AccountsPage')).AccountsPage,
}));
const AccountDetailPage = lazy(async () => ({
  default: (await import('./pages/AccountDetailPage')).AccountDetailPage,
}));
const ContactsPage = lazy(async () => ({
  default: (await import('./pages/ContactsPage')).ContactsPage,
}));
const ContactDetailPage = lazy(async () => ({
  default: (await import('./pages/ContactDetailPage')).ContactDetailPage,
}));
const OpportunitiesPage = lazy(async () => ({
  default: (await import('./pages/OpportunitiesPage')).OpportunitiesPage,
}));
const OpportunityDetailPage = lazy(async () => ({
  default: (await import('./pages/OpportunityDetailPage')).OpportunityDetailPage,
}));
const KanbanPage = lazy(async () => ({ default: (await import('./pages/KanbanPage')).KanbanPage }));
const ActivitiesPage = lazy(async () => ({
  default: (await import('./pages/ActivitiesPage')).ActivitiesPage,
}));
const PartnersPage = lazy(async () => ({
  default: (await import('./pages/PartnersPage')).PartnersPage,
}));
const PartnerDetailPage = lazy(async () => ({
  default: (await import('./pages/PartnerDetailPage')).PartnerDetailPage,
}));
const ReservationsPage = lazy(async () => ({
  default: (await import('./pages/ReservationsPage')).ReservationsPage,
}));
const ReservationDetailPage = lazy(async () => ({
  default: (await import('./pages/ReservationDetailPage')).ReservationDetailPage,
}));
const PipelineAdminPage = lazy(async () => ({
  default: (await import('./pages/PipelineAdminPage')).PipelineAdminPage,
}));
const UsersAdminPage = lazy(async () => ({
  default: (await import('./pages/UsersAdminPage')).UsersAdminPage,
}));
const PoliciesAdminPage = lazy(async () => ({
  default: (await import('./pages/PoliciesAdminPage')).PoliciesAdminPage,
}));
const AuditPage = lazy(async () => ({ default: (await import('./pages/AuditPage')).AuditPage }));
const DemoDataAdminPage = lazy(async () => ({
  default: (await import('./pages/DemoDataAdminPage')).DemoDataAdminPage,
}));
const HelpCenterPage = lazy(async () => ({
  default: (await import('./pages/HelpCenterPage')).HelpCenterPage,
}));
const HelpArticlePage = lazy(async () => ({
  default: (await import('./pages/HelpArticlePage')).HelpArticlePage,
}));
const AdminPage = lazy(async () => ({ default: (await import('./pages/AdminPage')).AdminPage }));
// CERES-UX-001: a jornada de Negócios (processo em seis fases).
const NegociosPage = lazy(async () => ({ default: (await import('./pages/NegociosPage')).NegociosPage }));
// CERES-UX-001: a memória comercial (empresa em foco + linha do tempo).
const RelacionamentosPage = lazy(async () => ({
  default: (await import('./pages/RelacionamentosPage')).RelacionamentosPage,
}));
// CERES-UX-001: conversas com clientes e com o CERES.
const ConversasPage = lazy(async () => ({ default: (await import('./pages/ConversasPage')).ConversasPage }));
// CERES-UX-001: o lançador do "Mais".
const MaisPage = lazy(async () => ({ default: (await import('./pages/MaisPage')).MaisPage }));
const RecursosAdminPage = lazy(async () => ({
  default: (await import('./pages/RecursosAdminPage')).RecursosAdminPage,
}));
const PlanoEUsoPage = lazy(async () => ({
  default: (await import('./pages/PlanoEUsoPage')).PlanoEUsoPage,
}));
const FilaDeEventosPage = lazy(async () => ({
  default: (await import('./pages/FilaDeEventosPage')).FilaDeEventosPage,
}));
const MemoriaPage = lazy(async () => ({
  default: (await import('./pages/MemoriaPage')).MemoriaPage,
}));
const AprovacoesPage = lazy(async () => ({
  default: (await import('./pages/AprovacoesPage')).AprovacoesPage,
}));
const ReceitaPage = lazy(async () => ({
  default: (await import('./pages/ReceitaPage')).ReceitaPage,
}));
const CustomerSuccessPage = lazy(async () => ({
  default: (await import('./pages/CustomerSuccessPage')).CustomerSuccessPage,
}));
const ReunioesPage = lazy(async () => ({
  default: (await import('./pages/ReunioesPage')).ReunioesPage,
}));
const PropostasPage = lazy(async () => ({
  default: (await import('./pages/PropostasPage')).PropostasPage,
}));
const ContratosPage = lazy(async () => ({
  default: (await import('./pages/ContratosPage')).ContratosPage,
}));
const AgentesPage = lazy(async () => ({
  default: (await import('./pages/AgentesPage')).AgentesPage,
}));
const ContratoPage = lazy(async () => ({
  default: (await import('./pages/ContratoPage')).ContratoPage,
}));
const CadenciasPage = lazy(async () => ({
  default: (await import('./pages/CadenciasPage')).CadenciasPage,
}));
const CaixaDeEntradaPage = lazy(async () => ({
  default: (await import('./pages/CaixaDeEntradaPage')).CaixaDeEntradaPage,
}));
const CatalogoComercialPage = lazy(async () => ({
  default: (await import('./pages/CatalogoComercialPage')).CatalogoComercialPage,
}));
const CatalogoItemPage = lazy(async () => ({
  default: (await import('./pages/CatalogoItemPage')).CatalogoItemPage,
}));
const ComissoesPage = lazy(async () => ({
  default: (await import('./pages/ComissoesPage')).ComissoesPage,
}));
const IcpPage = lazy(async () => ({ default: (await import('./pages/IcpPage')).IcpPage }));
const ImportacaoPage = lazy(async () => ({
  default: (await import('./pages/ImportacaoPage')).ImportacaoPage,
}));
/*
 * CERES-EST-004 §10 — a Inteligência Comercial entra pelo ponto público do
 * módulo. As páginas continuam preguiçosas: o `lazy` foi para lá junto.
 */
const RelatoriosPage = lazy(async () => ({
  default: (await import('./pages/RelatoriosPage')).RelatoriosPage,
}));
const CarteiraDePreVendasPage = lazy(async () => ({
  default: (await import('./pages/CarteiraDePreVendasPage')).CarteiraDePreVendasPage,
}));
const ConfigurarPreVendasPage = lazy(async () => ({
  default: (await import('./pages/ConfigurarPreVendasPage')).ConfigurarPreVendasPage,
}));
const TagsAdminPage = lazy(async () => ({
  default: (await import('./pages/TagsAdminPage')).TagsAdminPage,
}));
const IntegracoesAdminPage = lazy(async () => ({
  default: (await import('./pages/IntegracoesAdminPage')).IntegracoesAdminPage,
}));
const AgentesAdminPage = lazy(async () => ({
  default: (await import('./pages/AgentesAdminPage')).AgentesAdminPage,
}));
const QualidadeAdminPage = lazy(async () => ({
  default: (await import('./pages/QualidadeAdminPage')).QualidadeAdminPage,
}));
const PropostaPage = lazy(async () => ({
  default: (await import('./pages/PropostaPage')).PropostaPage,
}));
const ComposicaoDaPropostaPage = lazy(async () => ({
  default: (await import('./pages/ComposicaoDaPropostaPage')).ComposicaoDaPropostaPage,
}));
const ModelosDePropostaPage = lazy(async () => ({
  default: (await import('./pages/ModelosDePropostaPage')).ModelosDePropostaPage,
}));
const ModeloDePropostaPage = lazy(async () => ({
  default: (await import('./pages/ModeloDePropostaPage')).ModeloDePropostaPage,
}));
const GerarPropostaPage = lazy(async () => ({
  default: (await import('./pages/GerarPropostaPage')).GerarPropostaPage,
}));
const EditorDaPropostaPage = lazy(async () => ({
  default: (await import('./pages/EditorDaPropostaPage')).EditorDaPropostaPage,
}));
const AssistentePage = lazy(async () => ({
  default: (await import('./pages/AssistentePage')).AssistentePage,
}));
const MeuPerfilPage = lazy(async () => ({
  default: (await import('./pages/MeuPerfilPage')).MeuPerfilPage,
}));
const PreferenciasPage = lazy(async () => ({
  default: (await import('./pages/PreferenciasPage')).PreferenciasPage,
}));
// CERES-ADM-002 — Administração da plataforma: moldura e telas próprias.
const PlataformaShell = lazy(async () => ({
  default: (await import('./components/plataforma/PlataformaShell')).PlataformaShell,
}));
const PlataformaEntradaPage = lazy(async () => ({
  default: (await import('./pages/PlataformaEntradaPage')).PlataformaEntradaPage,
}));
const PlataformaVisaoGeralPage = lazy(async () => ({
  default: (await import('./pages/PlataformaVisaoGeralPage')).PlataformaVisaoGeralPage,
}));
const PlataformaTenantsPage = lazy(async () => ({
  default: (await import('./pages/PlataformaTenantsPage')).PlataformaTenantsPage,
}));
const PlataformaTenantPage = lazy(async () => ({
  default: (await import('./pages/PlataformaTenantPage')).PlataformaTenantPage,
}));
const PlataformaPessoasPage = lazy(async () => ({
  default: (await import('./pages/PlataformaPessoasPage')).PlataformaPessoasPage,
}));
const PlataformaSaudePage = lazy(async () => ({
  default: (await import('./pages/PlataformaSaudePage')).PlataformaSaudePage,
}));
const PlataformaEventosPage = lazy(async () => ({
  default: (await import('./pages/PlataformaEventosPage')).PlataformaEventosPage,
}));
// CERES-WA-001: Integrações → WhatsApp.
const PlataformaWhatsappPage = lazy(async () => ({
  default: (await import('./pages/PlataformaWhatsappPage')).PlataformaWhatsappPage,
}));
// CERES-ADM-004: casos, acessos e sessão de suporte.
const PlataformaSuportePage = lazy(async () => ({
  default: (await import('./pages/PlataformaSuportePage')).PlataformaSuportePage,
}));
const PlataformaCasoPage = lazy(async () => ({
  default: (await import('./pages/PlataformaCasoPage')).PlataformaCasoPage,
}));
const PlataformaSessaoDeSuportePage = lazy(async () => ({
  default: (await import('./pages/PlataformaSessaoDeSuportePage')).PlataformaSessaoDeSuportePage,
}));

/** Prefixo da área da plataforma — o único que o contexto da plataforma alcança. */
const AREA_DA_PLATAFORMA = '/admin/plataforma';

function naAreaDaPlataforma(pathname: string): boolean {
  return pathname === AREA_DA_PLATAFORMA || pathname.startsWith(`${AREA_DA_PLATAFORMA}/`);
}

/**
 * CERES-ADM-002: a área da plataforma tem DUAS faces. No contexto da
 * plataforma, a moldura e as telas; em qualquer outro, a porta — entrar é
 * sempre uma decisão explícita, nunca efeito de abrir um endereço.
 */
function AreaDaPlataforma(): JSX.Element {
  const { user } = useSession();
  // Fora do AppShell, o `<Suspense>` dele não alcança: a área tem o seu.
  return (
    <Suspense fallback={<FullPageLoader />}>
      {user?.contexto === 'PLATFORM' ? <PlataformaShell /> : <PlataformaEntradaPage />}
    </Suspense>
  );
}

// CERES-022 — a Carteira de pré-vendas substituiu as cinco telas da CERES-021.

function FullPageLoader(): JSX.Element {
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-sunken text-ink-muted">
      <Spinner className="h-6 w-6" />
      <span className="sr-only">Carregando…</span>
    </div>
  );
}

function ProtectedRoute({ children }: { children: ReactNode }): JSX.Element {
  const { user, legal, loading } = useSession();
  const location = useLocation();

  if (loading) return <FullPageLoader />;
  if (user === null) {
    // Preserva o destino para voltar depois do login — COM a query string:
    // o consentimento OAuth (/conectar-agente) vive nos parâmetros, e um
    // retorno sem eles seria uma tela que esqueceu por que existe.
    return <Navigate to="/entrar" replace state={{ from: location.pathname + location.search }} />;
  }

  /**
   * MODO RESTRITO: para onde a pessoa é levada, e por quê.
   *
   * Quatro condições tiram a sessão do uso normal, e o guarda de sessão do
   * servidor as avalia NESTA ordem, liberando só as rotas de uma por vez. O
   * bloqueio real é lá; isto aqui é o caminho — sem ele, a pessoa entraria no
   * painel e receberia 403 em cada consulta, sem nada dizendo o que fazer.
   *
   * UM DESTINO, E NÃO QUATRO DESVIOS INDEPENDENTES. Antes eram quatro `if`
   * seguidos, cada um comparando o caminho atual com o seu. A combinação de
   * duas condições produzia LAÇO: com senha temporária e segundo fator pendente,
   * `/trocar-senha` desviava para `/verificar-codigo` (porque ali o primeiro
   * `if` já estava satisfeito) e `/verificar-codigo` desviava de volta. O mesmo
   * acontecia com o aceite expulsando quem ia trocar a senha — para uma tela cujo
   * POST a API recusa. Escolher UM destino, na ordem do servidor, elimina a
   * classe inteira desses defeitos.
   */
  const destinoRestrito = user.mustChangePassword
    ? '/trocar-senha'
    : user.mfaPending
      ? '/verificar-codigo'
      : user.mfaSetupRequired
        ? '/configurar-mfa'
        : user.organizationSelectionRequired === true
          ? '/escolher-organizacao'
          : // CERES-ADM-002: o aceite é do contexto de um tenant; a plataforma
            // não tem tenant, e o servidor não o exige lá.
            !legal.tudoAceito && user.contexto !== 'PLATFORM'
            ? '/aceite'
            : null;

  // A escolha de contexto tem uma porta ao lado: fundar a própria empresa.
  const alternativaPermitida =
    destinoRestrito === '/escolher-organizacao' && location.pathname === '/nova-empresa';
  if (destinoRestrito !== null && location.pathname !== destinoRestrito && !alternativaPermitida) {
    return <Navigate to={destinoRestrito} replace />;
  }

  // CERES-ADM-002: no contexto da plataforma, nada do tenant abre — a API
  // recusaria. Qualquer endereço fora da área leva de volta a ela.
  if (destinoRestrito === null && user.contexto === 'PLATFORM' && !naAreaDaPlataforma(location.pathname)) {
    return <Navigate to={AREA_DA_PLATAFORMA} replace />;
  }

  return <>{children}</>;
}

function PublicOnlyRoute({ children }: { children: ReactNode }): JSX.Element {
  const { user, loading } = useSession();
  if (loading) return <FullPageLoader />;
  if (user !== null) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/**
 * Rota que exige permissão.
 *
 * O bloqueio de verdade está no servidor: cada rota administrativa recusa quem
 * não pode, venha o pedido de um clique ou de uma URL digitada na barra. Isto
 * aqui existe pelo motivo oposto — para que a pessoa receba uma FRASE em vez de
 * uma tela quebrada. Sem ele, quem digitasse /admin/usuarios sem permissão veria
 * a página montar e depois encher de erros de API, o que parece defeito do
 * sistema em vez de acesso negado.
 */
function ComPermissao({
  permissao,
  children,
}: {
  permissao: string | string[];
  children: ReactNode;
}): JSX.Element {
  const { can, canAny } = useSession();
  const liberado = Array.isArray(permissao) ? canAny(permissao) : can(permissao);
  if (liberado) return <>{children}</>;
  return (
    <div className="mx-auto max-w-lg rounded-lg border border-line bg-surface p-6 text-center">
      <h1 className="text-sm font-semibold text-ink">Acesso negado</h1>
      <p className="mt-1 text-xs text-ink-muted">
        Seu perfil não tem permissão para esta área. Se você precisa dela, fale com quem administra
        os acessos deste CRM.
      </p>
      <Link to="/" className="btn-secondary btn-sm mt-3 inline-flex">
        Voltar ao painel
      </Link>
    </div>
  );
}

/**
 * Rota que exige CAPACIDADE contratada (CERES-018A).
 *
 * Irmã de `ComPermissao`, e pelo mesmo motivo: o servidor recusa de qualquer
 * jeito, mas quem digita o endereço de um recurso fora do plano merece uma
 * frase em vez de uma tela que monta e enche de erro. Permissão e capacidade
 * são eixos independentes — ter `icp:read` no perfil não significa que a
 * organização contratou scoring —, então as duas negativas
 * existem e dizem coisas diferentes.
 *
 * A exigência de cada caminho vem da MESMA lista que desenha o menu, para não
 * haver dois mapas discordando sobre o mesmo recurso.
 */
function ComCapacidade({
  children,
  capacidade: exigida,
}: {
  children: ReactNode;
  /**
   * CERES-037 — A CAPACIDADE DECLARADA, quando o caminho não a revela.
   *
   * O mapa derivado da navegação responde por PREFIXO, e é isso que faz
   * `/empresas/123` herdar a exigência de `/empresas`. Para a composição de
   * proposta o prefixo responde ERRADO: `/propostas/:id/composicao` herdaria
   * `proposal_registry`, que é do núcleo, e o módulo contratado ficaria aberto
   * para quem não o contratou. O menu lateral não muda — geração nunca foi
   * item de menu —, então a exigência precisa ser dita na rota.
   */
  capacidade?: string;
}): JSX.Element {
  const { hasFeature, sessaoPronta } = useSession();
  const { pathname } = useLocation();
  const capacidade = exigida ?? capacidadeDaRota(pathname);

  /**
   * FAIL-CLOSED ONDE A CAPACIDADE É EXPLÍCITA.
   *
   * Um módulo contratado não abre por ausência, erro ou sessão a meio caminho:
   * enquanto a lista de capacidades não estiver resolvida, a resposta é não.
   * O mapa derivado mantém o comportamento de sempre — `hasFeature` já é
   * fail-closed por si, e exigir sessão resolvida ali barraria o núcleo.
   */
  if (exigida !== undefined && !sessaoPronta) return <FullPageLoader />;

  if (capacidade === null || hasFeature(capacidade)) return <>{children}</>;

  return (
    <div className="mx-auto max-w-lg rounded-lg border border-line bg-surface p-6 text-center">
      <h1 className="text-sm font-semibold text-ink">{t('plano.recursoForaDoPlano')}</h1>
      <p className="mt-1 text-xs text-ink-muted">{t('plano.recursoForaDoPlanoTexto')}</p>
      <Link to="/" className="btn-secondary btn-sm mt-3 inline-flex">
        {t('plano.voltarAoPainel')}
      </Link>
    </div>
  );
}

/**
 * A tabela de rotas, separada do roteador.
 *
 * O roteador de navegador depende de `window.history` e não pode ser montado em
 * teste; a TABELA pode. Separá-los é o que permite provar o gate de aceite e os
 * demais desvios (senha temporária, segundo fator) contra as rotas REAIS, em vez
 * de contra uma cópia mantida à parte que envelheceria em silêncio.
 */
export const rotas: RouteObject[] = [
  // Documentos legais: PÚBLICOS. Quem ainda não tem conta precisa poder ler os
  // termos antes de aceitar.
  // Páginas públicas (§21.12). Sem sessão de propósito: o caso de uso é
  // exatamente o de quem ainda não entrou — e uma página de status que exige
  // login é inútil justamente quando ela importa.
  { path: '/sobre', element: <ApresentacaoPage /> },
  { path: '/status', element: <StatusPage /> },
  { path: '/acessibilidade', element: <AcessibilidadePage /> },
  { path: '/contato', element: <ContatoPage /> },
  { path: '/legal', element: <LegalPage /> },
  { path: '/legal/:chave', element: <LegalPage /> },
  // Endereço público e estável da Política de Privacidade (cadastrado na Meta).
  { path: '/privacidade', element: <LegalPage chaveFixa="politica-de-privacidade" /> },
  {
    path: '/entrar',
    element: (
      <PublicOnlyRoute>
        <LoginPage />
      </PublicOnlyRoute>
    ),
  },
  {
    // CERES-008b: cadastro self-service — quem já tem sessão não recria conta.
    path: '/criar-conta',
    element: (
      <PublicOnlyRoute>
        <CriarContaPage />
      </PublicOnlyRoute>
    ),
  },
  {
    // CERES-ACC-005: empresa e modalidade, depois de confirmar o e-mail (ou
    // retomando com e-mail e senha). Quem já tem sessão não recria conta.
    path: '/criar-conta/empresa',
    element: (
      <PublicOnlyRoute>
        <CadastroDaEmpresaPage />
      </PublicOnlyRoute>
    ),
  },
  {
    // Chega pelo link do e-mail de confirmação do cadastro.
    path: '/confirmar-cadastro',
    element: <ConfirmarCadastroPage />,
  },
  {
    path: '/redefinir-senha',
    element: <PasswordResetPage />,
  },
  {
    // Primeiro acesso do usuário convidado (CERES-005): mesmo fluxo do link de
    // redefinição, com o texto do convite — quem nunca entrou não "redefine".
    path: '/convite',
    element: <PasswordResetPage modo="convite" />,
  },
  {
    // Consentimento OAuth do Agent Gateway: chega pelo conector externo; sem
    // sessão, o guarda manda ao login e traz de volta (a autoridade é o login).
    path: '/conectar-agente',
    element: (
      <ProtectedRoute>
        <ConectarAgentePage />
      </ProtectedRoute>
    ),
  },
  {
    // Fora do AppShell de propósito: oferecer os menus aqui prometeria acesso
    // que a API vai negar, e a pessoa concluiria que o sistema está quebrado.
    path: '/trocar-senha',
    element: (
      <ProtectedRoute>
        <ChangePasswordPage />
      </ProtectedRoute>
    ),
  },
  {
    // Fora do AppShell: a sessão está pendente e a API recusa o resto. Mostrar
    // os menus prometeria acesso que o servidor vai negar.
    path: '/verificar-codigo',
    element: (
      <ProtectedRoute>
        <MfaChallengePage />
      </ProtectedRoute>
    ),
  },
  {
    // Fora do AppShell (CERES-008): com mais de uma organização e nenhuma
    // escolhida, a API recusa tudo que é de dados. A escolha vem ANTES do
    // aceite, na mesma ordem do guarda do servidor.
    path: '/escolher-organizacao',
    element: (
      <ProtectedRoute>
        <EscolherOrganizacaoPage />
      </ProtectedRoute>
    ),
  },
  {
    // CERES-ACC-005-OPER-001: quem já tem conta cadastra outra empresa. Fora do
    // AppShell, como a escolha: também é alcançável sem organização ativa (o
    // servidor libera só as rotas de fundação no contexto neutro).
    path: '/nova-empresa',
    element: (
      <ProtectedRoute>
        <CadastroDaEmpresaPage modo="usuario" />
      </ProtectedRoute>
    ),
  },
  {
    // Fora do AppShell, como a troca de senha e o desafio de segundo fator: a
    // sessão está restrita e a API recusa o resto. Oferecer os menus aqui
    // prometeria acesso que o servidor vai negar.
    path: '/aceite',
    element: (
      <ProtectedRoute>
        <AceiteDeTermosPage />
      </ProtectedRoute>
    ),
  },
  {
    // CERES-ADM-002: fora do AppShell — nenhum menu comercial, nenhum tenant.
    // A autorização real é do servidor (papel E contexto da plataforma).
    path: AREA_DA_PLATAFORMA,
    element: (
      <ProtectedRoute>
        <AreaDaPlataforma />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <PlataformaVisaoGeralPage /> },
      { path: 'tenants', element: <PlataformaTenantsPage /> },
      { path: 'tenants/:id', element: <PlataformaTenantPage /> },
      { path: 'pessoas', element: <PlataformaPessoasPage /> },
      { path: 'saude', element: <PlataformaSaudePage /> },
      { path: 'eventos', element: <PlataformaEventosPage /> },
      { path: 'integracoes/whatsapp', element: <PlataformaWhatsappPage /> },
      { path: 'suporte', element: <PlataformaSuportePage /> },
      { path: 'suporte/sessao/:id', element: <PlataformaSessaoDeSuportePage /> },
      { path: 'suporte/:id', element: <PlataformaCasoPage /> },
    ],
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <ComCapacidade>
          <AppShell />
        </ComCapacidade>
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <HojePage /> },
      // CERES-UX-001: módulos, registros, gestão e configuração fora da rotina.
      { path: 'mais', element: <MaisPage /> },
      // CERES-UX-001: as jornadas (Negócios, Conversas, Relacionamentos).
      {
        path: 'negocios',
        element: (
          <ComPermissao permissao="opportunity:read">
            <NegociosPage />
          </ComPermissao>
        ),
      },
      // Sem ComPermissao: a página tem DUAS experiências com acessos diferentes
      // (comunicações; canal WhatsApp) e trata cada uma — e a falta das duas.
      { path: 'conversas', element: <ConversasPage /> },
      {
        path: 'relacionamentos',
        element: (
          <ComPermissao permissao="account:read">
            <RelacionamentosPage />
          </ComPermissao>
        ),
      },
      { path: 'empresas', element: <AccountsPage /> },
      { path: 'empresas/:id', element: <AccountDetailPage /> },
      { path: 'contatos', element: <ContactsPage /> },
      { path: 'contatos/:id', element: <ContactDetailPage /> },
      { path: 'oportunidades', element: <OpportunitiesPage /> },
      { path: 'oportunidades/:id', element: <OpportunityDetailPage /> },
      { path: 'pipeline', element: <KanbanPage /> },
      { path: 'atividades', element: <ActivitiesPage /> },
      { path: 'parceiros', element: <PartnersPage /> },
      { path: 'parceiros/:id', element: <PartnerDetailPage /> },
      { path: 'reservas', element: <ReservationsPage /> },
      { path: 'reservas/:id', element: <ReservationDetailPage /> },
      /*
       * CERES-028 — OS ENDEREÇOS DO PLAYBOOK CONTINUAM VALENDO.
       *
       * O módulo deixou de existir; os links que as pessoas salvaram, não.
       * Cada rota antiga leva à área equivalente do Catálogo unificado, e não
       * a uma tela de erro nem à raiz — cair na home é o mesmo que quebrar o
       * favorito, só que sem avisar.
       *
       *   /playbook                  → Catálogo (Ofertas)
       *   /playbook/materiais        → Biblioteca comercial
       *   /playbook/secao/:kind      → Método comercial ou Ofertas, conforme o
       *                                tipo de conteúdo (ver o componente)
       *   /playbook/produto/:oferta  → a ficha daquela oferta
       *   /playbooks                 → Governança (o acervo aprovado)
       */
      { path: 'playbook', element: <Navigate to="/catalogo-comercial" replace /> },
      {
        path: 'playbook/materiais',
        element: <Navigate to="/catalogo-comercial/biblioteca" replace />,
      },
      { path: 'playbook/secao/:kind', element: <RedirecionarSecaoDoPlaybook /> },
      { path: 'playbook/produto/:ofertaId', element: <RedirecionarFichaDaOferta /> },
      {
        path: 'admin',
        element: (
          <ComPermissao permissao={PERMISSOES_ADMIN}>
            <AdminPage />
          </ComPermissao>
        ),
      },
      {
        path: 'admin/pipelines',
        element: (
          <ComPermissao permissao="pipeline:configure">
            <PipelineAdminPage />
          </ComPermissao>
        ),
      },
      {
        path: 'admin/usuarios',
        element: (
          <ComPermissao permissao="user:manage">
            <UsuariosDoTenantOuDoCanal />
          </ComPermissao>
        ),
      },
      {
        path: 'admin/organizacao',
        element: (
          <ComPermissao permissao={['user:manage', 'role:manage']}>
            <AdministracaoDaOrganizacaoPage />
          </ComPermissao>
        ),
      },
      // CERES-ADM-003: a tela legada de organizações saiu; criar e cadastrar
      // tenant é da administração da plataforma. CERES-WA-002 §18: este é só o
      // REDIRECIONAMENTO de favorito antigo para a rota canônica — nenhuma tela
      // ou link interno aponta para cá (exceção registrada em rotas-alcancaveis).
      { path: 'admin/organizacoes', element: <Navigate to="/admin/plataforma/tenants" replace /> },
      { path: 'convites', element: <ConvitesRecebidosPage /> },
      {
        path: 'admin/tags',
        element: (
          <ComPermissao permissao="tag:assign">
            <TagsAdminPage />
          </ComPermissao>
        ),
      },
      {
        path: 'admin/regras-comerciais',
        element: (
          <ComPermissao permissao="policy:read">
            <PoliciesAdminPage />
          </ComPermissao>
        ),
      },
      // Dentro do shell de propósito, ao contrário da troca de senha: esta
      // também é a tela de gestão do próprio fator para quem já cadastrou, e
      // essa pessoa navega normalmente. Em modo restrito o shell não atrapalha
      // porque ele não faz requisição nenhuma, e clicar em qualquer item do menu
      // cai neste mesmo `ProtectedRoute`, que traz a pessoa de volta para cá —
      // sem uma única tela de erro no caminho.
      { path: 'configurar-mfa', element: <MfaSetupPage /> },
      {
        path: 'admin/auditoria',
        element: (
          <ComPermissao permissao="audit:read">
            <AuditPage />
          </ComPermissao>
        ),
      },
      {
        path: 'admin/dados-demonstracao',
        element: (
          <ComPermissao permissao="demo:manage">
            <DemoDataAdminPage />
          </ComPermissao>
        ),
      },
      {
        path: 'admin/medidas',
        element: (
          <ComPermissao permissao="feature:manage">
            <MedidasAdminPage />
          </ComPermissao>
        ),
      },
      {
        path: 'admin/eventos',
        element: (
          <ComPermissao permissao="event:read">
            <FilaDeEventosPage />
          </ComPermissao>
        ),
      },
      {
        path: 'admin/memoria',
        element: (
          <ComPermissao permissao="memory:read">
            <MemoriaPage />
          </ComPermissao>
        ),
      },
      {
        path: 'admin/recursos',
        element: (
          <ComPermissao permissao="feature:manage">
            <RecursosAdminPage />
          </ComPermissao>
        ),
      },
      {
        /*
         * CERES-EST-002 — PLANO E USO.
         *
         * SEM `ComPermissao`: qualquer pessoa da organização pode ver o que ela
         * contratou e quanto usa. As AÇÕES dentro da tela é que exigem
         * `organization:manage`, e o servidor as recusa de qualquer forma.
         *
         * Também sem gate de capacidade: é justamente a tela que explica por
         * que os módulos pararam de funcionar quando a assinatura é suspensa.
         * Trancá-la atrás do que ela explica seria deixar a chave do lado de
         * dentro.
         */
        path: 'plano',
        element: <PlanoEUsoPage />,
      },
      { path: 'assistente', element: <AssistentePage /> },
      {
        // FORA de /admin, de propósito: falar com um agente é trabalho, e não
        // configuração. Enterrada na administração, a página existia e ninguém
        // a alcançava — que foi exatamente o que a navegação em produção achou.
        path: 'agentes',
        element: (
          <ComPermissao permissao="agent:use">
            <AgentesPage />
          </ComPermissao>
        ),
      },
      {
        /*
         * A BIBLIOTECA DE MODELOS (CERES-038).
         *
         * Antes de `propostas/:id`, porque `modelos` é segmento estático e
         * `:id` é uuid — sem esta ordem, a biblioteca cairia na ficha de uma
         * proposta que não existe. A capacidade é declarada: `/propostas`
         * herdaria `proposal_registry`, que é do núcleo, e abriria o módulo
         * contratado para quem não o tem.
         */
        path: 'propostas/modelos',
        element: (
          <ComCapacidade capacidade="proposals">
            <ComPermissao permissao="proposal_template:read">
              <ModelosDePropostaPage />
            </ComPermissao>
          </ComCapacidade>
        ),
      },
      {
        path: 'propostas/modelos/:id',
        element: (
          <ComCapacidade capacidade="proposals">
            <ComPermissao permissao="proposal_template:read">
              <ModeloDePropostaPage />
            </ComPermissao>
          </ComCapacidade>
        ),
      },
      {
        // O editor e a ficha são a MESMA tela: separá-las criaria duas telas
        // com o mesmo conteúdo e uma delas ficaria para trás. O endereço
        // `/editar` continua valendo porque é para onde a criação leva.
        path: 'propostas/modelos/:id/editar',
        element: (
          <ComCapacidade capacidade="proposals">
            <ComPermissao permissao="proposal_template:read">
              <ModeloDePropostaPage />
            </ComPermissao>
          </ComCapacidade>
        ),
      },
      {
        /*
         * GERAR PROPOSTA NO CERES (CERES-039).
         *
         * Antes de `propostas/:id` pelo mesmo motivo de `modelos`: `gerar` é
         * segmento estático, e `:id` é uuid. A capacidade é declarada aqui —
         * compor documento é do módulo contratado, e herdar o gate do prefixo
         * `/propostas` abriria a geração para quem só registra.
         */
        path: 'propostas/gerar',
        element: (
          <ComCapacidade capacidade="proposals">
            <ComPermissao permissao="proposal:create">
              <GerarPropostaPage />
            </ComPermissao>
          </ComCapacidade>
        ),
      },
      {
        path: 'propostas/:id',
        element: (
          <ComPermissao permissao="proposal:read">
            <PropostaPage />
          </ComPermissao>
        ),
      },
      {
        /*
         * O EDITOR DA PROPOSTA GERADA (CERES-039).
         *
         * Endereçado pela PROPOSTA, e não pelo rascunho: quem volta amanhã tem
         * um link estável, e a versão em construção é resolvida lá dentro. Um
         * endereço por rascunho daria um link novo a cada versão — e o antigo
         * abriria um documento já fechado.
         */
        path: 'propostas/:id/editor',
        element: (
          <ComCapacidade capacidade="proposals">
            <ComPermissao permissao="proposal:read">
              <EditorDaPropostaPage />
            </ComPermissao>
          </ComCapacidade>
        ),
      },
      {
        /*
         * A COMPOSIÇÃO DE PROPOSTA DENTRO DO CERES (CERES-037).
         *
         * Endereço próprio porque são duas telas da mesma proposta: a ficha
         * documental responde "o que foi para o cliente?", esta responde "como
         * está ficando o documento?". Só a segunda pertence ao módulo
         * contratado `proposals`, e é por isso que a capacidade vem declarada
         * aqui em vez de herdada do prefixo `/propostas`.
         */
        path: 'propostas/:id/composicao',
        element: (
          <ComCapacidade capacidade="proposals">
            <ComPermissao permissao="proposal:read">
              <ComposicaoDaPropostaPage />
            </ComPermissao>
          </ComCapacidade>
        ),
      },
      {
        // Fica FORA de /admin: aprovar por alçada é trabalho comercial de quem
        // responde pelo resultado, e não configuração do sistema. Enterrá-la na
        // administração faria o gestor procurar no lugar errado.
        path: 'aprovacoes',
        element: (
          <ComPermissao permissao="approval:grant">
            <AprovacoesPage />
          </ComPermissao>
        ),
      },
      {
        path: 'propostas',
        element: (
          <ComPermissao permissao="proposal:read">
            <PropostasPage />
          </ComPermissao>
        ),
      },
      {
        path: 'contratos',
        element: (
          <ComPermissao permissao="contract:read">
            <ContratosPage />
          </ComPermissao>
        ),
      },
      {
        // A lista passou a GERAR minutas, e um documento gerado precisa poder
        // ser aberto: sem esta rota, o próprio produto levava a "página não
        // encontrada" logo depois de criar o contrato.
        path: 'contratos/:id',
        element: (
          <ComPermissao permissao="contract:read">
            <ContratoPage />
          </ComPermissao>
        ),
      },
      {
        path: 'cadencias',
        element: (
          <ComPermissao permissao="cadence:read">
            <CadenciasPage />
          </ComPermissao>
        ),
      },
      {
        path: 'caixa-de-entrada',
        element: (
          <ComPermissao permissao="communication:read">
            <CaixaDeEntradaPage />
          </ComPermissao>
        ),
      },
      {
        /*
         * O ENDEREÇO ANTIGO CONTINUA VALENDO (CERES-022 §3.3).
         *
         * `/leads` está em favoritos, em links colados em conversas e no guia
         * de primeiro acesso. Devolver 404 puniria quem guardou o caminho; o
         * redirecionamento leva para onde o trabalho passou a acontecer. A
         * página e as rotas da API do módulo anterior continuam existindo.
         */
        path: 'leads',
        element: <Navigate to="/pre-vendas" replace />,
      },
      /*
       * PRÉ-VENDAS (CERES-022): UMA tela operacional e uma de configuração.
       *
       * `ComPermissao` guarda a permissão; a CAPACIDADE é guardada por
       * `ComCapacidade`, que envolve a área protegida inteira e deriva a
       * exigência da mesma lista que desenha o menu.
       *
       * Os quatro endereços antigos redirecionam em vez de morrer: cada um
       * aponta para o lugar onde aquele trabalho passou a acontecer, e dois
       * deles carregam o destino dentro da própria Carteira.
       */
      {
        path: 'pre-vendas',
        element: (
          <ComPermissao permissao="pre_sales:read">
            <CarteiraDePreVendasPage />
          </ComPermissao>
        ),
      },
      {
        path: 'pre-vendas/configuracoes',
        element: (
          <ComPermissao permissao="pre_sales:configure">
            <ConfigurarPreVendasPage />
          </ComPermissao>
        ),
      },
      { path: 'pre-vendas/prospeccao', element: <Navigate to="/pre-vendas" replace /> },
      { path: 'pre-vendas/listas', element: <Navigate to="/pre-vendas?adicionar=1" replace /> },
      {
        path: 'pre-vendas/cadencias',
        element: <Navigate to="/pre-vendas/configuracoes?secao=planos" replace />,
      },
      {
        path: 'pre-vendas/qualificacao',
        element: <Navigate to="/pre-vendas?visao=qualificacao" replace />,
      },
      {
        /*
         * CERES-026: `/catalogo` REDIRECIONA para o catálogo canônico.
         *
         * Existiam duas telas respondendo "o que a empresa vende": esta, das
         * ofertas versionadas (vazia em todo tenant — zero linhas próprias), e o
         * Catálogo comercial do menu, com os itens reais. Duas portas para o
         * mesmo cadastro produzem exatamente o que produziram: alguém clica numa
         * delas, vê "cadastre o primeiro produto" e conclui que perdeu os dez
         * serviços que cadastrou.
         *
         * O endereço continua valendo — link salvo não quebra. A entidade
         * `offering` também continua: propostas e preços versionados apontam
         * para ela. O que deixou de existir é a segunda porta de cadastro.
         */
        path: 'catalogo',
        element: <Navigate to="/catalogo-comercial" replace />,
      },
      {
        // CERES-004: Catálogo Comercial do CORE (o gate de feature é do
        // backend; aqui, como em todo o roteador, o gate é de permissão).
        path: 'catalogo-comercial',
        element: (
          <ComPermissao permissao="catalog:read">
            <CatalogoComercialPage />
          </ComPermissao>
        ),
      },
      /*
       * CERES-028 — AS QUATRO ÁREAS DO CATÁLOGO.
       *
       * Rotas ESTÁTICAS antes da paramétrica: o React Router classifica
       * segmento fixo acima de `:id`, então `/catalogo-comercial/metodo` nunca
       * é lido como a ficha de uma oferta chamada "metodo". A ordem aqui é
       * legibilidade; a garantia é do roteador.
       *
       * As três áreas de conhecimento exigem a capacidade `playbooks`, que o
       * servidor afirma e a tela repete. Sem ela, a área não renderiza — e a
       * negativa diz que o recurso está fora do plano, em vez de mostrar uma
       * tela vazia que parece defeito.
       */
      {
        path: 'catalogo-comercial/metodo',
        element: (
          <ComPermissao permissao="playbook:read">
            <CatalogoMetodoPage />
          </ComPermissao>
        ),
      },
      {
        path: 'catalogo-comercial/biblioteca',
        element: (
          <ComPermissao permissao="content:read">
            <CatalogoBibliotecaPage />
          </ComPermissao>
        ),
      },
      {
        path: 'catalogo-comercial/governanca',
        element: (
          <ComPermissao permissao="playbook:read">
            <CatalogoGovernancaPage />
          </ComPermissao>
        ),
      },
      {
        // A ficha da oferta: identidade, completude e as sete abas de
        // conhecimento. A fonte do conhecimento é a MESMA entidade que a
        // Governança administra — nenhuma cópia.
        path: 'catalogo-comercial/:id',
        element: (
          <ComPermissao permissao="catalog:read">
            <CatalogoItemPage />
          </ComPermissao>
        ),
      },
      {
        path: 'admin/importacao',
        element: (
          <ComPermissao permissao="import:run">
            <ImportacaoPage />
          </ComPermissao>
        ),
      },
      {
        path: 'icp',
        element: (
          <ComPermissao permissao="icp:read">
            <IcpPage />
          </ComPermissao>
        ),
      },
      {
        path: 'comissoes',
        element: (
          // `commission:read` está na leitura base: ver o PRÓPRIO extrato é
          // direito de quem vende. O que a pessoa enxerga dentro da tela é
          // decidido pelo servidor, e não por este portão.
          <ComPermissao permissao="commission:read">
            <ComissoesPage />
          </ComPermissao>
        ),
      },
      { path: 'playbooks', element: <Navigate to="/catalogo-comercial/governanca" replace /> },
      /*
       * CERES-DESAC-003: `/mercado` e todo o seu ramo (pesquisa, concorrentes,
       * dossiê e os redirecionamentos do antigo mapeamento) saíram com a
       * Inteligência de Mercado. Um endereço antigo cai na rota curinga, como
       * qualquer outro que não existe.
       */
      {
        path: 'admin/integracoes',
        element: (
          <ComPermissao permissao="integration:read">
            <IntegracoesAdminPage />
          </ComPermissao>
        ),
      },
      {
        path: 'admin/agentes',
        element: (
          <ComPermissao permissao="agent:govern">
            <AgentesAdminPage />
          </ComPermissao>
        ),
      },
      {
        path: 'admin/dados',
        element: (
          <ComPermissao permissao="account:read">
            <QualidadeAdminPage />
          </ComPermissao>
        ),
      },
      {
        path: 'relatorios',
        element: (
          <ComPermissao permissao="opportunity:read">
            <RelatoriosPage />
          </ComPermissao>
        ),
      },
      {
        path: 'receita',
        element: (
          <ComPermissao permissao="forecast:read">
            <ReceitaPage />
          </ComPermissao>
        ),
      },
      {
        path: 'customer-success',
        element: (
          <ComPermissao permissao="cs:read">
            <CustomerSuccessPage />
          </ComPermissao>
        ),
      },
      {
        path: 'reunioes',
        element: (
          <ComPermissao permissao="meeting:read">
            <ReunioesPage />
          </ComPermissao>
        ),
      },
      { path: 'meu-perfil', element: <MeuPerfilPage /> },
      { path: 'preferencias', element: <PreferenciasPage /> },
      // A ajuda fica dentro da area autenticada de proposito: ela descreve a
      // operacao interna, nao e material publico.
      { path: 'ajuda', element: <HelpCenterPage /> },
      { path: 'ajuda/:slug', element: <HelpArticlePage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];

export const router = createBrowserRouter(rotas);
